import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody } from "../../ui/dialog";
import Spinner from "../../spinner";
import { getHealthSummaryHistory } from "../../../services/patient/carePlan";
import styles from "./styles.module.css";

// The unit is added only to a plain number (or "120/80" for blood pressure).
// "N/A" and older free-text values (e.g. "120 lbs", "138/76 mmHg (clinic)")
// are shown exactly as saved, so the unit is never doubled.
const PLAIN_READING = /^\d+(\.\d+)?(\/\d+(\.\d+)?)?$/;

const hasReading = (row) => row?.value !== null && row?.value !== undefined && `${row.value}`.trim() !== "";

// SCRUM-1003: Height is shown in inches (whatever unit the API sends).
const UNIT_OVERRIDES = { height: "inches" };

const formatReading = (row, metric) => {
  if (!hasReading(row)) return "–";
  const value = `${row.value}`.trim();
  const unit = UNIT_OVERRIDES[metric] || row.unit;
  return unit && PLAIN_READING.test(value) ? `${value} ${unit}` : value;
};

/**
 * "Previous 1 year {Field}" popup (Health Summary history): the 12 months
 * before the selected care plan month for one physiological metric.
 *
 * field: { metric, label } of the clicked field, or null when closed.
 * month/year: the care plan month being viewed.
 */
const HealthSummaryHistoryModal = ({ field, onClose, month, year }) => {
  const patientId = sessionStorage.getItem("_pt_id");
  const open = !!field;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["healthSummaryHistory", patientId, field?.metric, month, year],
    queryFn: () => getHealthSummaryHistory({ metric: field.metric, month, year }),
    enabled: open && !!patientId,
    // Always fetch on open (the app-wide staleTime is 1 minute), so values
    // just saved or changed in another month show up straight away.
    staleTime: 0,
  });

  const rows = data?.data || [];
  const hasAnyReading = rows.some(hasReading);

  return (
    <Dialog open={open} setOpen={(value) => !value && onClose()}>
      <DialogContent className={styles.dialog}style={{ minWidth: "auto" }}>
        <DialogHeader>
          <DialogTitle className={styles.title}>Previous 1 year {field?.label}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading ? (
            <div className={styles.state}>
              <Spinner />
            </div>
          ) : isError ? (
            <p className={styles.state}>Could not load the history. Please try again.</p>
          ) : !hasAnyReading ? (
            <p className={styles.state}>No readings available for the past 1 year</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Readings</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.month}>
                    <td>{row.label}</td>
                    <td>{formatReading(row, field?.metric)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default HealthSummaryHistoryModal;
